/* biome-ignore-all lint/style/noParameterAssign: Need to pass ref for forwardRef */
/* biome-ignore-all lint/performance/noNamespaceImport: Cleaner when working with libraries */
/* biome-ignore-all lint: TODO: Leaving these for esz-fortune and kermentrout to handle */
'use client';

// TODO: This component needs to be updated to match other primitives and return just a Form Element
import type * as LabelPrimitive from '@radix-ui/react-label';
import { Slot } from '@radix-ui/react-slot';
import React from 'react';
import {
  Controller,
  type ControllerProps,
  type FieldPath,
  type FieldValues,
  FormProvider,
  useFormContext,
} from 'react-hook-form';
import * as Label from '@/app_components/ui/Label';

import { cn } from '@/app_lib/utils/utils';

export const Form = FormProvider;

type FormFieldContextValue<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> = {
  name: TName;
};

const FormFieldContext = React.createContext<FormFieldContextValue>({} as FormFieldContextValue);

export const FormField = <
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
  ...rest
}: ControllerProps<TFieldValues, TName>) => {
  return (
    <FormFieldContext.Provider value={{ name: rest.name }}>
      <Controller {...rest} />
    </FormFieldContext.Provider>
  );
};

type FormItemContextValue = {
  id: string;
};

const FormItemContext = React.createContext<FormItemContextValue>({} as FormItemContextValue);

export const useFormField = () => {
  const fieldContext = React.useContext(FormFieldContext);
  const itemContext = React.useContext(FormItemContext);
  const { getFieldState, formState } = useFormContext();

  const fieldState = getFieldState(fieldContext.name, formState);

  if (!fieldContext) {
    throw new Error('useFormField should be used within <FormField>');
  }

  const { id } = itemContext;

  return {
    formDescriptionId: `${id}-form-item-description`,
    formItemId: `${id}-form-item`,
    formMessageId: `${id}-form-item-message`,
    id,
    name: fieldContext.name,
    ...fieldState,
  };
};

export const FormItem = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...rest }, ref) => {
    const id = React.useId();

    return (
      <FormItemContext.Provider value={{ id }}>
        <div
          className={cn('space-y-2', className)}
          ref={ref}
          {...rest}
        />
      </FormItemContext.Provider>
    );
  },
);
FormItem.displayName = 'FormItem';

export const FormLabel = React.forwardRef<
  React.ComponentRef<typeof LabelPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof LabelPrimitive.Root> & { required?: boolean }
>(({ className, required, ...rest }, ref) => {
  const { error, formItemId } = useFormField();

  return (
    <Label.Root
      className={cn(error && 'text-error-700', className)}
      htmlFor={formItemId}
      ref={ref}
      required={required}
      {...rest}
    />
  );
});
FormLabel.displayName = 'FormLabel';

export const FormControl = React.forwardRef<
  React.ComponentRef<typeof Slot>,
  React.ComponentPropsWithoutRef<typeof Slot>
>(({ ...rest }, ref) => {
  const { error, formItemId, formDescriptionId, formMessageId } = useFormField();

  return (
    <Slot
      aria-describedby={error ? `${formDescriptionId} ${formMessageId}` : `${formDescriptionId}`}
      aria-invalid={Boolean(error)}
      id={formItemId}
      ref={ref}
      {...rest}
    />
  );
});
FormControl.displayName = 'FormControl';

export const FormDescription = React.forwardRef<HTMLParagraphElement, React.HTMLAttributes<HTMLParagraphElement>>(
  ({ className, ...rest }, ref) => {
    const { formDescriptionId } = useFormField();

    return (
      <p
        className={cn('typography-sm text-neutrals-400 font-graphik-condensed', className)}
        id={formDescriptionId}
        ref={ref}
        {...rest}
      />
    );
  },
);
FormDescription.displayName = 'FormDescription';

export interface FormMessageProps extends React.HTMLAttributes<HTMLParagraphElement> {
  success?: boolean;
  successMessage?: string;
}

export const FormMessage = React.forwardRef<HTMLParagraphElement, FormMessageProps>(
  ({ className, children, success, successMessage, ...rest }, ref) => {
    const { error, formMessageId } = useFormField();
    let body: React.ReactNode = null;
    let colorClass = 'text-[0.8rem] font-medium';

    if (error) {
      body = String(error?.message);
      colorClass += ' text-error-700';
    } else if (success && successMessage) {
      body = successMessage;
      colorClass += ' text-success-700';
    } else if (children) {
      body = children;
      colorClass += ' text-neutrals-700';
    }

    if (!body) {
      return null;
    }

    return (
      <p
        className={cn(colorClass, className)}
        id={formMessageId}
        ref={ref}
        {...rest}
      >
        {body}
      </p>
    );
  },
);
FormMessage.displayName = 'FormMessage';
